import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useAppDispatch, useAppSelector } from '@/app/hooks';
import type { RootState } from '@/app/store';
import { fetchAddress, updateAddress } from '@/features/billing/billingThunks';
import { selectAddress, selectAddressState } from '@/features/billing/billingSlice';
import { Button } from '@/components/ui/button';
import Title from '@/components/ui/Title';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { InputLabel } from '@/components/ui/InputLabel';
import { toast } from 'react-toastify';
import Loader from '@/components/ui/Loader';
import type { StripeAddressInput } from '@/features/billing/types';
import { CountrySelectSearch } from '@/components/ui/CountrySelectSearch';
import { useTranslation } from 'react-i18next';
import {
  checkTaxIdentifier,
  getTaxIdentifierFormat,
  normalizeTaxIdentifier,
} from '@/lib/validation/taxIdentifier';

type AddressFormData = {
  companyName: string;
  line1: string;
  line2?: string;
  city: string;
  postalCode: string;
  country: string;
  taxId?: string;
};

export default function AddressBilling() {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('settings');
  const workspaceId = useAppSelector((s: RootState) => s.workspace.currentWorkspace?.id);
  const address = useAppSelector(selectAddress);
  const { loading: addressLoading } = useAppSelector(selectAddressState);

  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const addressSchema = useMemo(
    () =>
      z.object({
        companyName: z
          .string()
          .min(1, t('address.validation.companyNameRequired'))
          .refine((val) => val.trim().length > 0, {
            message: t('address.validation.companyNameRequired'),
          })
          .max(200, t('address.validation.max200')),
        line1: z
          .string()
          .min(1, t('address.validation.line1Required'))
          .refine((val) => val.trim().length > 0, {
            message: t('address.validation.line1Required'),
          })
          .max(255, t('address.validation.max255')),
        line2: z.string().max(255, t('address.validation.max255')).optional(),
        city: z
          .string()
          .min(1, t('address.validation.cityRequired'))
          .refine((val) => val.trim().length > 0, { message: t('address.validation.cityRequired') })
          .max(255, t('address.validation.max255')),
        postalCode: z
          .string()
          .min(1, t('address.validation.postalCodeRequired'))
          .refine((val) => val.trim().length > 0, {
            message: t('address.validation.postalCodeRequired'),
          })
          .max(20, t('address.validation.max20')),
        country: z
          .string()
          .length(2, t('address.validation.countryRequired'))
          .refine((val) => val.trim().length === 2, {
            message: t('address.validation.countryRequired'),
          })
          .transform((v) => v.trim().toUpperCase()),
        taxId: z.string().max(50, t('address.validation.max50')).optional(),
      }),
    [t],
  );

  const addressSchemaWithTaxId = useMemo(
    () =>
      addressSchema.superRefine((data, ctx) => {
        const status = checkTaxIdentifier(data.country, data.taxId);
        if (status === 'valid' || status === 'empty') return;
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['taxId'],
          message:
            status === 'unsupported-country'
              ? t('address.validation.taxIdUnsupported')
              : t('address.validation.taxIdInvalid', {
                  example: getTaxIdentifierFormat(data.country)?.example,
                }),
        });
      }),
    [addressSchema, t],
  );

  const form = useForm<AddressFormData>({
    resolver: zodResolver(addressSchemaWithTaxId),
    defaultValues: {
      companyName: '',
      line1: '',
      line2: '',
      city: '',
      postalCode: '',
      country: 'FR',
      taxId: '',
    },
    mode: 'onChange',
  });

  useEffect(() => {
    if (workspaceId) {
      dispatch(fetchAddress(workspaceId));
    }
  }, [workspaceId, dispatch]);

  useEffect(() => {
    if (address && isDialogOpen) {
      form.reset({
        companyName: address.companyName ?? '',
        line1: address.line1 ?? '',
        line2: address.line2 ?? '',
        city: address.city ?? '',
        postalCode: address.postalCode ?? '',
        country: (address.country ?? 'FR').toUpperCase(),
        // Sur un pays non pris en charge, on n'affiche pas la valeur existante : elle bloquerait
        // l'enregistrement de l'adresse. Ne pas la renvoyer ne la supprime pas chez Stripe.
        taxId: getTaxIdentifierFormat(address.country) ? (address.taxId ?? '') : '',
      });
      // Une adresse déjà incomplète en base doit montrer ses erreurs tout de suite,
      // sinon le bouton d'enregistrement se retrouve désactivé sans explication.
      void form.trigger();
    }
  }, [address, isDialogOpen, form]);

  const selectedCountry = useWatch({ control: form.control, name: 'country' });
  const taxIdFormat = getTaxIdentifierFormat(selectedCountry);

  const onSubmit = async (data: AddressFormData) => {
    if (!workspaceId) return;

    const input: StripeAddressInput = {
      companyName: (data.companyName || '').trim() || undefined,
      line1: data.line1.trim(),
      line2: (data.line2 || '').trim() || undefined,
      city: data.city.trim(),
      postalCode: data.postalCode.trim(),
      country: data.country.trim().toUpperCase(),
      taxId: normalizeTaxIdentifier(data.country, data.taxId) || undefined,
    };

    const result = await dispatch(updateAddress({ workspaceId, input }));
    if (updateAddress.fulfilled.match(result)) {
      setIsDialogOpen(false);
      toast.success(t('address.toast.success'));
    } else {
      toast.error(t('address.toast.error'));
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Title level={2}>{t('address.title')}</Title>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button variant="outline" disabled={addressLoading}>
              {t('address.edit')}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t('address.dialogTitle')}</DialogTitle>
            </DialogHeader>

            <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
              <div>
                <Controller
                  name="companyName"
                  control={form.control}
                  render={({ field }) => (
                    <InputLabel
                      label={t('address.companyName')}
                      value={field.value ?? ''}
                      onChange={(e) => field.onChange(e.target.value)}
                    />
                  )}
                />
                {form.formState.errors.companyName && (
                  <p className="mt-1 text-sm text-error">
                    {form.formState.errors.companyName.message}
                  </p>
                )}
              </div>

              <div>
                <Controller
                  name="line1"
                  control={form.control}
                  render={({ field }) => (
                    <InputLabel
                      label={t('address.line1')}
                      value={field.value}
                      onChange={(e) => field.onChange(e.target.value)}
                    />
                  )}
                />
                {form.formState.errors.line1 && (
                  <p className="mt-1 text-sm text-error">{form.formState.errors.line1.message}</p>
                )}
              </div>

              <div>
                <Controller
                  name="line2"
                  control={form.control}
                  render={({ field }) => (
                    <InputLabel
                      label={t('address.line2')}
                      value={field.value ?? ''}
                      onChange={(e) => field.onChange(e.target.value)}
                    />
                  )}
                />
                {form.formState.errors.line2 && (
                  <p className="mt-1 text-sm text-error">{form.formState.errors.line2.message}</p>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Controller
                    name="city"
                    control={form.control}
                    render={({ field }) => (
                      <InputLabel
                        label={t('address.city')}
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                      />
                    )}
                  />
                  {form.formState.errors.city && (
                    <p className="mt-1 text-sm text-error">{form.formState.errors.city.message}</p>
                  )}
                </div>
                <div>
                  <Controller
                    name="postalCode"
                    control={form.control}
                    render={({ field }) => (
                      <InputLabel
                        label={t('address.postalCode')}
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                      />
                    )}
                  />
                  {form.formState.errors.postalCode && (
                    <p className="mt-1 text-sm text-error">
                      {form.formState.errors.postalCode.message}
                    </p>
                  )}
                </div>
              </div>

              <div className="w-full">
                <Controller
                  name="country"
                  control={form.control}
                  render={({ field }) => (
                    <CountrySelectSearch
                      value={field.value ?? ''}
                      onChange={(value) => {
                        field.onChange(value);
                        if (!getTaxIdentifierFormat(value)) form.setValue('taxId', '');
                        void form.trigger('taxId');
                      }}
                      label={t('address.country')}
                      className="w-full"
                      valueFormat="code"
                    />
                  )}
                />
                {form.formState.errors.country && (
                  <p className="mt-1 text-sm text-error">{form.formState.errors.country.message}</p>
                )}
              </div>

              <div>
                <Controller
                  name="taxId"
                  control={form.control}
                  render={({ field }) => (
                    <InputLabel
                      label={
                        taxIdFormat?.stripeType === 'eu_vat'
                          ? t('address.vatNumber')
                          : t('address.taxId')
                      }
                      value={field.value ?? ''}
                      onChange={(e) => field.onChange(e.target.value)}
                      maxLength={50}
                      disabled={!taxIdFormat}
                      placeholder={taxIdFormat?.example}
                    />
                  )}
                />
                {form.formState.errors.taxId ? (
                  <p className="mt-1 text-sm text-error">{form.formState.errors.taxId.message}</p>
                ) : (
                  !taxIdFormat && (
                    <p className="mt-1 text-sm text-secondary">{t('address.taxIdUnsupported')}</p>
                  )
                )}
              </div>

              <DialogFooter className="flex flex-col sm:flex-row items-center gap-4">
                <Button
                  type="button"
                  variant="outline"
                  className="w-full sm:flex-1"
                  onClick={() => setIsDialogOpen(false)}
                >
                  {t('address.cancel')}
                </Button>
                <Button
                  type="submit"
                  className="w-full sm:flex-1"
                  disabled={form.formState.isSubmitting || !form.formState.isValid}
                >
                  {form.formState.isSubmitting ? <Loader /> : t('address.save')}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <div>
        {addressLoading && !address ? (
          <div className="flex justify-center py-8">
            <Loader />
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            <InputLabel
              label={t('address.companyName')}
              value={address?.companyName ?? ''}
              disabled
            />
            <InputLabel label={t('address.line1')} value={address?.line1 ?? ''} disabled />
            <InputLabel label={t('address.line2')} value={address?.line2 ?? ''} disabled />
            <div className="grid grid-cols-2 gap-4">
              <InputLabel label={t('address.city')} value={address?.city ?? ''} disabled />
              <InputLabel
                label={t('address.postalCode')}
                value={address?.postalCode ?? ''}
                disabled
              />
            </div>
            {address?.country ? (
              <CountrySelectSearch
                value={address.country}
                onChange={() => {}}
                label={t('address.country')}
                className="w-full"
                valueFormat="code"
                disabled
              />
            ) : (
              <InputLabel label={t('address.country')} value="" disabled />
            )}
            <InputLabel label={t('address.taxId')} value={address?.taxId ?? ''} disabled />
          </div>
        )}
      </div>
    </div>
  );
}
